/**
 * The events FTUX sends ows-uxo, built the way the server validates them (momentum-online
 * ows_uxo/server/src/events.ts): a strict object, so no field beyond these; control and label on clicked and qa
 * only, action on did only, outcome on ended only, target on missing only; a qa control is jump or restart; piece,
 * step, the brand and Heimdall's ids are null when there is none, never left out. buildUxoEvent is pure.
 * nextUxoContext stamps the moment: the tab (one per page load, made on its first event), the tab's counter, a fresh
 * event id, the time, where the page is and Heimdall's ids for it. Nothing runs at import time, as these modules load for every owner.
 */
import { THEME_ATTR } from '../../../theme/themeConfig';
import { LUMIO_DESKTOP, MOBILE_MQ } from '../../../theme/tokens';
import {
  FTUX_FLOW,
  FTUX_SCRIPT_VERSION,
  type BrandPieceId,
  type FtuxAction,
  type FtuxControlId,
  type FtuxEventType,
  type FtuxOutcome,
  type FtuxQaControl,
  type FtuxStep,
} from '../catalog';
import type { FtuxTargetId } from '../targets';
import { heimdallIds, type HeimdallIds } from './heimdallIds';

export type UxoViewport = 'desktop' | 'tablet' | 'phone';
export type UxoTheme = 'light' | 'dark';

/** Where in the flow it happened: the piece in play (null outside the pieces) and the step (a flow step, or the
 *  piece's view key). */
type UxoWhere = { piece: BrandPieceId | null; step: FtuxStep | null };

/** What happened, by type, with the fields that type reports. A label is the control's English copy. */
export type UxoEventInput = UxoWhere &
  (
    | { type: 'shown' }
    | { type: 'clicked'; control: FtuxControlId; label?: string }
    | { type: 'did'; action: FtuxAction }
    | { type: 'ended'; outcome: FtuxOutcome }
    | { type: 'finished' }
    | { type: 'qa'; control: FtuxQaControl; label?: string }
    | { type: 'missing'; target: FtuxTargetId }
  );

/** The caller's part of an event: the run and the brand it belongs to. */
export type UxoRunContext = {
  /** When the run started, epoch ms. */
  run: number;
  /** The run's brand id, or null: Lumio's hello and the map are the owner's run, with no brand. */
  brandId: string | null;
  /** The run's brand name (casino_data.details.brand), or null. */
  brandName: string | null;
  /** The owner-hub session (state.session.sessionId), the one pulsar-identity hands Heimdall, or null. */
  sessionId: string | null;
};

/** Everything an event carries besides what happened: the run and the brand, then the stamp of the moment. */
export type UxoEventContext = UxoRunContext &
  HeimdallIds & {
    eventId: string;
    /** ISO 8601, on the owner's clock. */
    at: string;
    tab: string;
    seq: number;
    /** location.pathname, never the query. */
    path: string;
    viewport: UxoViewport;
    theme?: UxoTheme;
  };

/** One event as the server takes it (its ClientEvent), in its field order. */
export type UxoClientEvent = {
  eventId: string;
  at: string;
  tab: string;
  seq: number;
  flow: typeof FTUX_FLOW;
  v: typeof FTUX_SCRIPT_VERSION;
  run: number;
  type: FtuxEventType;
  piece: BrandPieceId | null;
  step: FtuxStep | null;
  control?: FtuxControlId;
  label?: string;
  action?: FtuxAction;
  outcome?: FtuxOutcome;
  /** On missing only: the `data-ftux` target the step looked for. */
  target?: FtuxTargetId;
  brandId: string | null;
  brandName: string | null;
  path: string;
  viewport: UxoViewport;
  theme?: UxoTheme;
  /** Heimdall's ids for the page: its session_id, anonymous_session_id and anonymous_user_id. */
  sessionId: string | null;
  anonymousSessionId: string | null;
  anonymousUserId: string | null;
};

/** The server's longest strings, in UTF-16 code units as it counts them. */
const LABEL_MAX = 160;
const BRAND_NAME_MAX = 120;
const BRAND_ID_MAX = 64;
const PATH_MAX = 300;

/** `value` cut to `max`, never between the two halves of a surrogate pair. */
const clip = (value: string, max: number): string => {
  if (value.length <= max) return value;
  const cut = value.slice(0, max);
  const last = cut.charCodeAt(cut.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
};

const clipOrNull = (value: string | null, max: number): string | null => (value ? clip(value, max) : null);

/** The fields only some types carry. */
const ownFields = (
  input: UxoEventInput,
): Pick<UxoClientEvent, 'control' | 'label' | 'action' | 'outcome' | 'target'> => {
  switch (input.type) {
    case 'clicked':
    case 'qa':
      return input.label ? { control: input.control, label: clip(input.label, LABEL_MAX) } : { control: input.control };
    case 'did':
      return { action: input.action };
    case 'ended':
      return { outcome: input.outcome };
    case 'missing':
      return { target: input.target };
    default:
      return {};
  }
};

/** The event `input` describes, at `context`. Pure. */
export const buildUxoEvent = (input: UxoEventInput, context: UxoEventContext): UxoClientEvent => ({
  eventId: context.eventId,
  at: context.at,
  tab: context.tab,
  seq: context.seq,
  flow: FTUX_FLOW,
  v: FTUX_SCRIPT_VERSION,
  run: context.run,
  type: input.type,
  piece: input.piece,
  step: input.step,
  ...ownFields(input),
  brandId: clipOrNull(context.brandId, BRAND_ID_MAX),
  brandName: clipOrNull(context.brandName, BRAND_NAME_MAX),
  path: clip(context.path, PATH_MAX),
  viewport: context.viewport,
  ...(context.theme ? { theme: context.theme } : {}),
  sessionId: context.sessionId,
  anonymousSessionId: context.anonymousSessionId,
  anonymousUserId: context.anonymousUserId,
});

/** owners-hub's layout right now: the phone shell up to 899 px, the docked Lumio column from 1280 px, else tablet. */
export const viewportOf = (): UxoViewport => {
  if (typeof window.matchMedia !== 'function') return 'desktop';
  const matches = (query: string): boolean => window.matchMedia(query.replace(/^@media\s+/, '')).matches;
  if (matches(MOBILE_MQ)) return 'phone';
  return matches(LUMIO_DESKTOP) ? 'desktop' : 'tablet';
};

/** The studio's resolved theme (html[data-ows-theme]), when it has one. */
export const themeOf = (): UxoTheme | undefined => {
  const theme = document.documentElement.getAttribute(THEME_ATTR);
  return theme === 'light' || theme === 'dark' ? theme : undefined;
};

/** Sixteen random bytes from crypto.getRandomValues, which every origin offers (Math.random where crypto is missing). */
const randomBytes = (): Uint8Array => {
  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === 'function') return globalThis.crypto.getRandomValues(bytes);
  return bytes.map(() => Math.floor(Math.random() * 256));
};

/**
 * An RFC 4122 version 4 UUID, the only form the server takes for eventId and tab: crypto.randomUUID where the origin
 * offers it (secure contexts only; plain http on a LAN IP has none), else built from random bytes.
 */
export const uuidV4 = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  // The version (4) in byte 6's high nibble, the variant (binary 10) in byte 8's top bits.
  const bytes = randomBytes().map((byte, index) => {
    if (index === 6) return (byte & 0x0f) | 0x40;
    if (index === 8) return (byte & 0x3f) | 0x80;
    return byte;
  });
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** This page load's tab, made on its first event, and its event counter (from 0). */
let tab: string | null = null;
let seq = 0;

/**
 * The context of the next event: `base`, the tab and its counter, a fresh event id, the time, where the page is and
 * Heimdall's ids for it (base's session checked as they are).
 */
export const nextUxoContext = (base: UxoRunContext): UxoEventContext => {
  const tabId = tab ?? uuidV4();
  tab = tabId;
  const context: UxoEventContext = {
    ...base,
    eventId: uuidV4(),
    at: new Date().toISOString(),
    tab: tabId,
    seq,
    // The path alone: the query can carry invite tokens.
    path: window.location.pathname,
    viewport: viewportOf(),
    theme: themeOf(),
    ...heimdallIds(base.sessionId),
  };
  seq += 1;
  return context;
};
